// Profile Management System
// Handles multiple user profiles with separate progress tracking

const PROFILES_KEY = 'kidsLearningProfiles';
const ACTIVE_PROFILE_KEY = 'activeProfileId';

/**
 * Get all profiles from localStorage
 * @returns {Array} Array of profile objects
 */
export const getProfiles = () => {
  try {
    const profiles = localStorage.getItem(PROFILES_KEY);
    return profiles ? JSON.parse(profiles) : [];
  } catch (error) {
    console.error('Error loading profiles:', error);
    return [];
  }
};

/**
 * Save profiles to localStorage
 * @param {Array} profiles - Array of profile objects
 */
const saveProfiles = (profiles) => {
  try {
    localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
  } catch (error) {
    console.error('Error saving profiles:', error);
  }
};

/**
 * Create a new profile
 * @param {Object} profileData - Profile information
 * @returns {Object} Created profile
 */
export const createProfile = ({ name, age, avatar, color }) => {
  const profiles = getProfiles();
  
  const newProfile = {
    id: `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: name.trim(),
    age: parseInt(age),
    avatar: avatar || '👤',
    color: color || '#3498db',
    createdAt: new Date().toISOString(),
    lastActive: new Date().toISOString(),
    userProgress: {},
    earnedBadges: [],
    stats: {
      totalQuizzes: 0,
      totalScore: 0,
      totalQuestions: 0,
      correctAnswers: 0,
      streak: 0,
      lastPlayedDate: null,
      totalTimeSpent: 0,
      favoriteCategory: null
    },
    settings: {
      soundEnabled: true,
      darkMode: false,
      difficulty: 'medium'
    }
  };

  profiles.push(newProfile);
  saveProfiles(profiles);
  
  return newProfile;
};

/**
 * Update an existing profile
 * @param {String} profileId - Profile ID
 * @param {Object} updates - Fields to update
 * @returns {Object} Updated profile
 */
export const updateProfile = (profileId, updates) => {
  const profiles = getProfiles();
  const profileIndex = profiles.findIndex(p => p.id === profileId);
  
  if (profileIndex === -1) {
    throw new Error('Profile not found');
  }

  profiles[profileIndex] = {
    ...profiles[profileIndex],
    ...updates,
    lastActive: new Date().toISOString()
  };

  saveProfiles(profiles);
  return profiles[profileIndex];
};

/**
 * Delete a profile
 * @param {String} profileId - Profile ID to delete
 * @returns {Boolean} Success status
 */
export const deleteProfile = (profileId) => {
  const profiles = getProfiles();
  const filteredProfiles = profiles.filter(p => p.id !== profileId);
  
  if (filteredProfiles.length === profiles.length) {
    return false; // Profile not found
  }

  saveProfiles(filteredProfiles);
  
  // If deleted profile was active, clear active profile
  const activeProfileId = getActiveProfileId();
  if (activeProfileId === profileId) {
    clearActiveProfile();
  }
  
  return true;
};

/**
 * Get active profile ID
 * @returns {String|null} Active profile ID
 */
export const getActiveProfileId = () => {
  return localStorage.getItem(ACTIVE_PROFILE_KEY);
};

/**
 * Get active profile object
 * @returns {Object|null} Active profile
 */
export const getActiveProfile = () => {
  const profileId = getActiveProfileId();
  if (!profileId) return null;
  
  const profiles = getProfiles();
  return profiles.find(p => p.id === profileId) || null;
};

/**
 * Set active profile
 * @param {String} profileId - Profile ID to activate
 * @returns {Object} Activated profile
 */
export const setActiveProfile = (profileId) => {
  const profiles = getProfiles();
  const profile = profiles.find(p => p.id === profileId);
  
  if (!profile) {
    throw new Error('Profile not found');
  }

  localStorage.setItem(ACTIVE_PROFILE_KEY, profileId);
  
  // Update last active time
  updateProfile(profileId, { lastActive: new Date().toISOString() });
  
  return profile;
};

/**
 * Clear active profile (logout)
 */
export const clearActiveProfile = () => {
  localStorage.removeItem(ACTIVE_PROFILE_KEY);
};

/**
 * Update profile progress
 * @param {String} profileId - Profile ID
 * @param {Object} progressData - Progress data to save
 */
export const updateProfileProgress = (profileId, progressData) => {
  const profiles = getProfiles();
  const profileIndex = profiles.findIndex(p => p.id === profileId);
  
  if (profileIndex === -1) {
    throw new Error('Profile not found');
  }

  profiles[profileIndex] = {
    ...profiles[profileIndex],
    userProgress: progressData.userProgress || profiles[profileIndex].userProgress,
    earnedBadges: progressData.earnedBadges || profiles[profileIndex].earnedBadges,
    lastActive: new Date().toISOString()
  };

  // Update stats
  if (progressData.userProgress) {
    const stats = calculateStats(progressData.userProgress);
    profiles[profileIndex].stats = {
      ...profiles[profileIndex].stats,
      ...stats
    };
  }

  saveProfiles(profiles);
};

/**
 * Calculate statistics from progress data
 * @param {Object} userProgress - User progress object
 * @returns {Object} Calculated statistics
 */
const calculateStats = (userProgress) => {
  let totalQuizzes = 0;
  let totalScore = 0;
  let totalQuestions = 0;
  let favoriteCategory = null;
  let maxQuizzes = 0;

  Object.entries(userProgress).forEach(([category, data]) => {
    totalQuizzes += data.totalQuizzes || 0;
    totalScore += data.totalScore || 0;
    totalQuestions += (data.totalQuizzes || 0) * 5; // Assuming 5 questions per quiz

    if ((data.totalQuizzes || 0) > maxQuizzes) {
      maxQuizzes = data.totalQuizzes;
      favoriteCategory = category;
    }
  });

  return {
    totalQuizzes,
    totalScore,
    totalQuestions,
    correctAnswers: totalScore,
    favoriteCategory
  };
};

/**
 * Update profile settings
 * @param {String} profileId - Profile ID
 * @param {Object} settings - Settings to update
 */
export const updateProfileSettings = (profileId, settings) => {
  const profiles = getProfiles();
  const profileIndex = profiles.findIndex(p => p.id === profileId);
  
  if (profileIndex === -1) {
    throw new Error('Profile not found');
  }

  profiles[profileIndex].settings = {
    ...profiles[profileIndex].settings,
    ...settings
  };

  saveProfiles(profiles);
};

/**
 * Get profile by ID
 * @param {String} profileId - Profile ID
 * @returns {Object|null} Profile object
 */
export const getProfileById = (profileId) => {
  const profiles = getProfiles();
  return profiles.find(p => p.id === profileId) || null;
};

/**
 * Check if profile name already exists
 * @param {String} name - Profile name to check
 * @param {String} excludeId - Profile ID to exclude from check (for updates)
 * @returns {Boolean} True if name exists
 */
export const profileNameExists = (name, excludeId = null) => {
  const profiles = getProfiles();
  return profiles.some(p => 
    p.name.toLowerCase() === name.toLowerCase().trim() && 
    p.id !== excludeId
  );
};

/**
 * Get profile count
 * @returns {Number} Number of profiles
 */
export const getProfileCount = () => {
  return getProfiles().length;
};

/**
 * Export profile data
 * @param {String} profileId - Profile ID
 * @returns {String} JSON string of profile data
 */
export const exportProfileData = (profileId) => {
  const profile = getProfileById(profileId);
  if (!profile) {
    throw new Error('Profile not found');
  }
  
  return JSON.stringify(profile, null, 2);
};

/**
 * Import profile data
 * @param {String} jsonData - JSON string of profile data
 * @returns {Object} Imported profile
 */
export const importProfileData = (jsonData) => {
  try {
    const profileData = JSON.parse(jsonData);
    
    // Generate new ID to avoid conflicts
    const newProfile = {
      ...profileData,
      id: `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      lastActive: new Date().toISOString()
    };
    
    const profiles = getProfiles();
    profiles.push(newProfile);
    saveProfiles(profiles);
    
    return newProfile;
  } catch (error) {
    throw new Error('Invalid profile data');
  }
};
